// Cliente HTTP del inspector-portal.
//
// Auth: flujo OAuth REAL Authorization Code + PKCE contra AuthSys (dogfooding
// del mismo contrato que usan las OTTs), con el client dev `cli_dev_inspector`.
// El code se extrae del `redirect_to` que devuelve /auth/login, sin navegar
// (mismo truco que login-portal → loginForAccessToken).
//
// Tokens en sessionStorage (se limpian al cerrar la tab) + store in-memory
// con subscribe. 401 → un intento de refresh (single-flight: la rotación de
// refresh tokens detecta reuso y revoca la sesión, así que JAMAS dos refresh
// en paralelo) → si falla, logout.

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

export const DEFAULT_PARTNER_ID =
  (import.meta.env.VITE_DEFAULT_PARTNER_ID as string | undefined) ?? 'lacorte-2026';

export const CLIENT_ID = 'cli_dev_inspector';

export class InspectorApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'InspectorApiError';
  }
}

// ---------------------------------------------------------------------------
// Session store
// ---------------------------------------------------------------------------

const SESSION_KEY = 'authsys-inspector-session';

export interface InspectorSession {
  access_token: string;
  refresh_token: string | null;
  /** epoch seconds */
  expires_at: number;
  partner_id: string;
  email: string;
}

let currentSession: InspectorSession | null = null;
const sessionListeners = new Set<(s: InspectorSession | null) => void>();

function persistSession(s: InspectorSession | null): void {
  try {
    if (s) sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // sessionStorage bloqueado (private mode) — memoria pura.
  }
}

function readPersistedSession(): InspectorSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InspectorSession;
    if (parsed.expires_at <= Math.floor(Date.now() / 1000) && !parsed.refresh_token) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getSession(): InspectorSession | null {
  if (currentSession) return currentSession;
  currentSession = readPersistedSession();
  return currentSession;
}

export function setSession(s: InspectorSession | null): void {
  currentSession = s;
  persistSession(s);
  for (const fn of sessionListeners) fn(s);
}

export function subscribeSession(fn: (s: InspectorSession | null) => void): () => void {
  sessionListeners.add(fn);
  return () => sessionListeners.delete(fn);
}

// ---------------------------------------------------------------------------
// PKCE + login (Auth Code sin redirect del browser)
// ---------------------------------------------------------------------------

function base64urlBytes(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generatePkce(): Promise<{ verifier: string; challenge: string }> {
  const verifier = base64urlBytes(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64urlBytes(new Uint8Array(digest)) };
}

export interface TosNotice {
  version: string;
  tos_url: string | null;
  status: 'grace' | 'expired';
  grace_deadline: string | null;
}

export interface ConsentRequired {
  consent_required: true;
  tos: TosNotice;
  consent_ticket: string;
}

export function isConsentRequired(r: unknown): r is ConsentRequired {
  return (
    typeof r === 'object' &&
    r !== null &&
    (r as { consent_required?: unknown }).consent_required === true
  );
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new InspectorApiError('network_error', `network request to ${path} failed`, 0);
  }
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new InspectorApiError(
      (json['error'] as string) ?? 'unknown_error',
      (json['error_description'] as string) ?? (json['message'] as string) ?? res.statusText,
      res.status,
    );
  }
  return json as T;
}

async function exchangeToken(
  partnerId: string,
  params: Record<string, string>,
): Promise<TokenResponse> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/partners/${encodeURIComponent(partnerId)}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: new URLSearchParams(params).toString(),
    });
  } catch {
    throw new InspectorApiError('network_error', 'network request to /oauth/token failed', 0);
  }
  const json = (await res.json().catch(() => ({}))) as Record<string, string>;
  if (!res.ok) {
    throw new InspectorApiError(
      json['error'] ?? 'token_error',
      json['error_description'] ?? json['message'] ?? 'token exchange failed',
      res.status,
    );
  }
  return json as unknown as TokenResponse;
}

function redirectUri(): string {
  return `${window.location.origin}/callback`;
}

export type LoginOutcome = { ok: true } | ConsentRequired;

/**
 * Login completo: /auth/login → (consent?) → code → /oauth/token → sesión.
 * Si el backend pide re-consentimiento de T&C devuelve ConsentRequired; el
 * caller muestra el interstitial y termina con completeConsent().
 */
export async function loginInspector(opts: {
  email: string;
  password: string;
  partnerId: string;
}): Promise<LoginOutcome> {
  const { verifier, challenge } = await generatePkce();
  const state = base64urlBytes(crypto.getRandomValues(new Uint8Array(16)));

  const result = await postJson<{ redirect_to?: string } | ConsentRequired>('/auth/login', {
    email: opts.email,
    password: opts.password,
    partner_id: opts.partnerId,
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
    code_challenge: challenge,
    code_challenge_method: 'S256',
    scope: 'openid profile offline_access',
    state,
  });

  if (isConsentRequired(result)) {
    pendingConsent = { verifier, partnerId: opts.partnerId, email: opts.email };
    return result;
  }
  await finishLogin(result, verifier, opts.partnerId, opts.email);
  return { ok: true };
}

// Contexto PKCE pendiente entre login → interstitial de consent → finish.
let pendingConsent: { verifier: string; partnerId: string; email: string } | null = null;

export async function completeConsent(consentTicket: string): Promise<void> {
  if (!pendingConsent) {
    throw new InspectorApiError('consent_state_lost', 'reintentá el login', 400);
  }
  const { verifier, partnerId, email } = pendingConsent;
  const result = await postJson<{ redirect_to?: string }>('/auth/consent-accept', {
    consent_ticket: consentTicket,
    action: 'accept',
  });
  pendingConsent = null;
  await finishLogin(result, verifier, partnerId, email);
}

async function finishLogin(
  result: { redirect_to?: string },
  verifier: string,
  partnerId: string,
  email: string,
): Promise<void> {
  if (!result.redirect_to) {
    throw new InspectorApiError('login_failed', 'el login no devolvió redirect_to', 500);
  }
  const code = new URL(result.redirect_to).searchParams.get('code');
  if (!code) throw new InspectorApiError('login_failed', 'no llegó authorization code', 500);

  const tokens = await exchangeToken(partnerId, {
    grant_type: 'authorization_code',
    code,
    code_verifier: verifier,
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
  });

  setSession({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token ?? null,
    expires_at: Math.floor(Date.now() / 1000) + (tokens.expires_in ?? 3600),
    partner_id: partnerId,
    email,
  });
}

// ---------------------------------------------------------------------------
// Refresh single-flight
// ---------------------------------------------------------------------------
// La rotación server-side invalida el refresh usado y DETECTA REUSO (revoca la
// sesión entera). Dos queries concurrentes que reciban 401 no pueden refrescar
// las dos: la promise compartida garantiza un solo intento.

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const session = getSession();
  if (!session?.refresh_token) return false;

  refreshInFlight ??= (async () => {
    try {
      const tokens = await exchangeToken(session.partner_id, {
        grant_type: 'refresh_token',
        refresh_token: session.refresh_token as string,
        client_id: CLIENT_ID,
      });
      setSession({
        ...session,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token ?? session.refresh_token,
        expires_at: Math.floor(Date.now() / 1000) + (tokens.expires_in ?? 3600),
      });
      return true;
    } catch {
      setSession(null);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export function logout(): void {
  setSession(null);
}

// ---------------------------------------------------------------------------
// Fetch autenticado (Bearer + retry-once tras refresh)
// ---------------------------------------------------------------------------

async function fetchJson<T>(path: string, retried = false): Promise<T> {
  const session = getSession();
  if (!session) throw new InspectorApiError('no_session', 'sin sesión activa', 401);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${session.access_token}` },
    });
  } catch {
    throw new InspectorApiError('network_error', `network request to ${path} failed`, 0);
  }

  if ((res.status === 401 || res.status === 400) && !retried) {
    // El backend responde invalid_request (400) para token expirado/invalido.
    const body = (await res.clone().json().catch(() => ({}))) as { error?: string };
    if (body.error === 'invalid_request' || res.status === 401) {
      if (await tryRefresh()) return fetchJson<T>(path, true);
      throw new InspectorApiError('session_expired', 'la sesión expiró — reingresá', 401);
    }
  }

  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new InspectorApiError(
      (json['error'] as string) ?? 'unknown_error',
      (json['error_description'] as string) ?? (json['message'] as string) ?? res.statusText,
      res.status,
    );
  }
  return json as T;
}

// ---------------------------------------------------------------------------
// Tipos del snapshot /inspector/me
// ---------------------------------------------------------------------------

export interface SectionUnavailable {
  available: false;
  error: string;
}

export function isUnavailable(v: unknown): v is SectionUnavailable {
  return typeof v === 'object' && v !== null && (v as { available?: unknown }).available === false;
}

export interface SnapshotSession {
  session_id: string;
  user_id: string;
  partner_id: string;
  client_id: string;
  device_id: string;
  scopes: string[];
}

export interface Profile {
  user_id: string;
  display_name: string | null;
  status: 'active' | 'blocked' | 'deactivated';
  email_verified: boolean;
}

export interface Account {
  email: string;
  first_name: string | null;
  last_name: string | null;
  marketing_opt_in: boolean;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface Subscription {
  subscription_id: string;
  partner_id: string;
  plan_id: string;
  plan_code: string | null;
  plan_name: string | null;
  status: string;
  started_at: string;
  current_period_end: string | null;
  grace_period_end: string | null;
  payment_provider: string;
}

export interface Entitlements {
  partner_id: string;
  user_status: string;
  subscription_status: string;
  has_pending_payment: boolean;
  plan_code: string | null;
  plan_display_name: string | null;
  max_concurrent_streams: number | null;
  max_devices: number | null;
}

export interface Payment {
  payment_id: string;
  subscription_id: string;
  partner_id: string;
  plan_id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  payment_provider: string;
  external_payment_id: string | null;
  failure_code: string | null;
  created_at: string;
}

export interface Device {
  device_id: string;
  device_type: string;
  device_model: string | null;
  device_version: string | null;
  partner_id: string;
  login_at: number;
  last_seen_at: number;
  ip_hash: string | null;
  user_agent: string | null;
}

export interface UserSession {
  session_id: string;
  partner_id: string;
  client_id: string;
  device_id: string;
  status: 'active' | 'rotated' | 'revoked';
  issued_at: number;
  expires_at: number;
  current: boolean;
}

export interface DeviceCode {
  status: 'pending' | 'approved' | 'consumed' | 'expired';
  partner_id: string;
  client_id: string;
  scope: string | null;
  device_name: string | null;
  expires_at: number;
  last_polled_at: number | null;
}

export interface TosAcceptance {
  partner_id: string;
  tos_version: string;
  accepted_at: string;
  ip: string | null;
  user_agent: string | null;
  source: string | null;
}

export interface Tos {
  partner_id: string;
  status: 'current' | 'grace' | 'expired';
  current_version: string | null;
  accepted_version: string | null;
  grace_deadline: string | null;
  tos_url: string | null;
  acceptances: TosAcceptance[];
}

/**
 * Ventana de gracia de verificación de email (migr. 0037). `seconds_remaining`
 * lo calcula Postgres — el panel descuenta desde ahí en vez de comparar contra
 * el reloj local.
 */
export interface VerificationGrace {
  partner_id: string;
  require_email_verification: boolean;
  email_verified: boolean;
  subscription_id: string | null;
  plan_id: string | null;
  plan_grace_hours: number | null;
  grace_until: string | null;
  seconds_remaining: number | null;
  active: boolean;
}

export interface EmailToken {
  kind: 'verification' | 'password_reset';
  partner_id: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  ip: string | null;
}

type Section<T> = T | SectionUnavailable | undefined;

export interface Snapshot {
  generated_at: string;
  environment: string;
  session: SnapshotSession;
  profile?: Section<Profile>;
  account?: Section<Account>;
  subscriptions?: Section<Subscription[]>;
  entitlements?: Section<Entitlements | null>;
  payments?: Section<Payment[]>;
  devices?: Section<Device[]>;
  sessions?: Section<UserSession[]>;
  device_codes?: Section<DeviceCode[]>;
  tos?: Section<Tos>;
  email_tokens?: Section<EmailToken[]>;
  verification_grace?: Section<VerificationGrace>;
}

export interface AuditEntry {
  audit_id: string;
  occurred_at: string;
  actor_type: string;
  actor_id: string | null;
  action: string;
  reason: string | null;
  ip: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown> | null;
}

export interface AuditResult {
  entries: AuditEntry[];
  count: number;
  limit_applied: number;
}

// ---------------------------------------------------------------------------
// Endpoints inspector
// ---------------------------------------------------------------------------

// `verification_grace` va en el poll corto a propósito: QA necesita ver la
// ventana vencerse en vivo, no 60 s después.
export const LIVE_SECTIONS = 'sessions,devices,device_codes,entitlements,verification_grace';

export async function fetchSnapshot(sections?: string): Promise<Snapshot> {
  const qs = sections ? `?sections=${encodeURIComponent(sections)}` : '';
  return fetchJson<Snapshot>(`/inspector/me${qs}`);
}

// ---------------------------------------------------------------------------
// Acciones de ensayo (POST /inspector/me/actions)
// ---------------------------------------------------------------------------

export type InspectorAction =
  | 'email_verify'
  | 'email_unverify'
  | 'subscriptions_cancel'
  | 'subscriptions_expire'
  | 'sessions_revoke_all'
  | 'email_tokens_expire'
  | 'entitlements_cache_invalidate'
  | 'webhook_simulate'
  | 'grace_set'
  | 'grace_expire'
  | 'grace_clear';

/** Debe coincidir 1:1 con SIMULATABLE_WEBHOOK_EVENTS en services/inspector/actions/handler.ts. */
export type SimulatableWebhookEvent =
  | 'subscription.activated'
  | 'subscription.renewed'
  | 'subscription.expired'
  | 'subscription.changed'
  | 'subscription.grace_period'
  | 'user.deactivated'
  | 'device.authorized'
  | 'webhook.ping';

export interface ActionResult {
  action: InspectorAction;
  [k: string]: unknown;
}

export async function runAction(
  action: InspectorAction,
  opts?: { webhookEvent?: SimulatableWebhookEvent; graceMinutes?: number },
): Promise<ActionResult> {
  const session = getSession();
  if (!session) throw new InspectorApiError('no_session', 'sin sesión activa', 401);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/inspector/me/actions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action,
        ...(opts?.webhookEvent ? { webhook_event: opts.webhookEvent } : {}),
        ...(opts?.graceMinutes !== undefined ? { grace_minutes: opts.graceMinutes } : {}),
      }),
    });
  } catch {
    throw new InspectorApiError('network_error', 'network request to /inspector/me/actions failed', 0);
  }
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new InspectorApiError(
      (json['error'] as string) ?? 'unknown_error',
      (json['error_description'] as string) ?? (json['message'] as string) ?? res.statusText,
      res.status,
    );
  }
  return json as unknown as ActionResult;
}

export interface AuditFilters {
  action?: string;
  from?: string; // ISO
  to?: string;   // ISO
  limit?: number;
}

export async function fetchAudit(filters: AuditFilters): Promise<AuditResult> {
  const params = new URLSearchParams();
  if (filters.action) params.set('action', filters.action);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return fetchJson<AuditResult>(`/inspector/me/audit${qs ? `?${qs}` : ''}`);
}
